<template>
  <div class="surveyTool" :class="{ surveyPage: showSurvey }">
    <div class="container" v-if="!showSurvey">
      <Navbar :isLandingPage="true" />
    </div>
    <div class="container" :class="{ center: !showSurvey || showLoader }">
      <Navbar :isLandingPage="false" v-if="showSurvey && !showLoader" />
      <div class="intro" v-if="!showSurvey">
        <h1 class="title typeWriter" ref="typeWriterText">
          <span class="screenReader">{{ survey?.surveyName }}</span>
          <span class="originalText">{{ survey?.surveyName }}</span>
          <span aria-hidden="true" class="animate"></span>
        </h1> 
        <div class="grid">
          <IntroGrid
            v-for="(introGrid, index) in survey?.introGrid"
            :key="index"
            :introGrid="introGrid"
          />
        </div>
        <div class="start-btn">
          <button
            @click.prevent="startSurvey"
          >
            Start <img src="/assets/images/icons/arrowRight-Primary.svg" alt="" />
          </button>
        </div>
      </div>

      <div class="survey" :class="{ loading: showLoader }" v-else>
        <Loader v-if="showLoader" />
        <div class="questions" :class="{ loading: showLoader }">
          <div 
            class="progress" 
            v-if="!updateSurvey" 
            :class="{ updateSurvey: updateSurvey }"
          >
            {{ activeQuestionIndex + 2 }} OF {{ survey?.questions.length + 1 }}
          </div>

          <form 
            method="get" 
            :action="formAction" 
            @submit.prevent="submitSurvey"
          >
            
            <fieldset 
              v-show="activeQuestionIndex === -1" 
              :class="{ updateSurvey: updateSurvey }"
            >
              <div class="question">
                <h2 class="title">{{ survey?.routingQuestion }}</h2>
                <div class="answers">
                  <div class="answer-wrapper" :class="{ twoColumns: survey?.routingAnswers.length > 6 }">
                    <div 
                      class="answer radio" 
                      v-for="(answer, index) in survey?.routingAnswers" 
                      :key="index" 
                      :class="{
                        checked: selectedRoutingAnswer === index
                      }"
                    >
                      <input
                        type="radio"
                        name="routing"
                        :id="index"
                        :value="index"
                        v-model="selectedRoutingAnswer"
                      /><label :for="index">{{ answer.answerText }}</label>
                    </div>
                  </div>  
                </div>
              </div>
              <div class="btns">
                <div class="continue-btn">
                  <button
                    :disabled="selectedRoutingAnswer === null"
                    @click.prevent="nextQuestion"
                  >
                    Continue <img src="/assets/images/icons/arrowRight-White-Thin.svg" alt="" />
              </button>
                </div>
                <div class="previous-btn">
                  <button @click.prevent="previousQuestion">
                    <img src="/assets/images/icons/arrowLeft-Charcoal.svg" alt="" />Previous
                  </button>
                </div>
              </div>
            </fieldset>
            <fieldset
              :class="{ updateSurvey: updateSurvey }"
              v-for="(question, index) in survey?.questions"
              :key="question.id"
              v-show="index === activeQuestionIndex && activeQuestionIndex >= 0"
            >
              <Question 
                :question="question"
                @selected-answers="
                  selectedAnswers => updateQuestionsAnswered(question.id, selectedAnswers)
                "
              />
              <div class="btns">
                <div class="continue-btn">
                  <button
                    @click.prevent="nextQuestion"
                    :disabled="isDisabled(question.id, question.isRequired)"
                    v-if="activeQuestionIndex !== survey?.questions.length - 1"
                  >
                    Continue <img src="/assets/images/icons/arrowRight-White-Thin.svg" alt="" />
                  </button>

                  <button
                    type="submit"
                    :disabled="isDisabled(question.id, question.isRequired)"
                    v-else
                  >
                    Discover how we can help you <img src="/assets/images/icons/arrowRight-White-Thin.svg" alt="" />
                  </button>
                </div>
                <div class="previous-btn">
                  <button
                    @click.prevent="previousQuestion"
                    v-show="activeQuestionIndex > -1"
                  >
                    <img src="/assets/images/icons/arrowLeft-Charcoal.svg" alt="" /> Previous
                  </button>
                </div>
              </div>
            </fieldset>
              <input type="hidden" name="surveyId" :value="id"/>
          </form>
        </div>
      </div>
    </div>

    <div class="lottieFile" v-if="showSurvey && !updateSurvey && !showLoader">
      <Vue3Lottie
        v-if="isMobile()"
        :animationLink="`/assets/lottie/surveyTool/mobile-${activeQuestionIndex + 2}.json`"
        :loop="false"
        height="auto"
      />
      <Vue3Lottie
        v-else
        :animationLink="`/assets/lottie/surveyTool/pc-long-${activeQuestionIndex + 2}.json`" 
        :loop="false"
        height="auto"
      />
    </div>
    <div class="factCard question">
      <div class="info" v-if="showSurvey && !showLoader">
        <div class="details">
          <img v-if="!updateSurvey" :src="`${factIconUrl}`" alt="" />
          <h2 v-if="!updateSurvey" class="factTitle">{{ factTitle }}</h2>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, PropType, Ref, watch, computed } from "vue";
import SurveyService from "./services/survey.api";
import { Survey } from "./models/Survey";
import Question from "./components/Question.vue";
import IntroGrid from "./components/IntroGrid.vue";
import Navbar from "./components/Navbar.vue";
import Loader from "./components/Loader.vue";
import { Vue3Lottie } from "vue3-lottie";
import 'vue3-lottie/dist/style.css';

export default {
  props: {
    id: {
      type: Number as PropType<number>,
      required: true
    }
  },
  components: {
    Question,
    IntroGrid,
    Navbar,
    Loader,
    Vue3Lottie
  },
  setup(props) {
    let survey: Ref<Survey> = ref(null);
    const selectedRoutingAnswer = ref<number | null>(null);
    const activeQuestionIndex = ref(-1);
    const showSurvey = ref(false);
    const typeWriterText = ref(null);
    const updateSurvey = ref(false);
    const showLoader = ref(false);
    const questionsAnswered = ref<number[]>([]);
    const id = props.id;

    const smallTablet = 600;
    const isMobile = () => screen.width < smallTablet;

    // define computed property for form action
    const formAction = computed(() => {     
        return survey.value?.routingAnswers[selectedRoutingAnswer.value]?.resultPage;
    });

    const factIconUrl = computed(() => {
      return activeQuestionIndex.value === -1
        ? survey.value?.routingFactIconUrl
        : survey.value?.questions[activeQuestionIndex.value]?.factIconUrl;
    });

    const factTitle = computed(() => {
      return activeQuestionIndex.value === -1
        ? survey.value?.routingFactTitle
        : survey.value?.questions[activeQuestionIndex.value]?.factTitle;
    });

    onMounted(async () => {
      try {
        SurveyService.getSurvey(props.id)
          .then((surveyResponse) => {
            survey.value = surveyResponse;
          })
          .catch((error) => {
            console.error(error);
          });
      } catch (error) {
        console.error(error);
      }
    });

    function nextQuestion() {
      if (activeQuestionIndex.value >= survey?.value.questions.length - 1) {
        return;
      }

      activeQuestionIndex.value++;

      updateSurvey.value = true;
      setTimeout(() => {
        updateSurvey.value = false;
        window.scrollTo(0, 0);
      }, 200);
    }

    function previousQuestion() {
      if (activeQuestionIndex.value <= - 1) {
        window.location.reload();
        return;
      }

      activeQuestionIndex.value--;

      updateSurvey.value = true;
      setTimeout(() => {
        updateSurvey.value = false;
        window.scrollTo(0, 0);
      }, 200);
    }

    function startSurvey() {
      showSurvey.value = true;
      window.scrollTo(0, 0);
    }

    function typeText() {
      const originalText: HTMLElement = typeWriterText.value.querySelector(".originalText")
      const text = survey.value?.surveyName;
      let i = 0;

      while (i < text.length) {
        const span = document.createElement("span");
        originalText.nextElementSibling.appendChild(span);
        span.innerHTML += text.charAt(i);
        i++;
      }

      originalText.remove();
    }

    function submitSurvey(event) {
      event.preventDefault();
      showLoader.value = true;

      setTimeout(() => {
        if(survey.value?.gatedFormUrl != "") {
          const gatedFormUrl = new URL(survey.value?.gatedFormUrl , window.location.origin);
          const encodedReturnUrl = buildEncodedReturnUrl(
            event.target as HTMLFormElement,
            survey.value?.gatedFormUrl
          );
          window.location.href = `${gatedFormUrl.pathname}?returnUrl=${encodedReturnUrl}`;
        }else{
          event.target.submit();
        }
      }, 3000);
    }

    watch(survey, () => {
      typeText();
    });

    function updateQuestionsAnswered(
      questionId: number,
      selectedAnswers: number[]
    ) {
      if (
        questionsAnswered.value.includes(questionId) &&
        selectedAnswers.length === 0
      ) {
        questionsAnswered.value = questionsAnswered.value.filter(
          id => id !== questionId
        );
      } else if (
        !questionsAnswered.value.includes(questionId) &&
        (selectedAnswers.length > 0 || typeof selectedAnswers === "number")
      ) {
        questionsAnswered.value.push(questionId);
      }
    }

    function isDisabled(questionId: number, isRequired: boolean): boolean {
      if (!isRequired) {
        return false;
      } else if (isRequired && questionsAnswered.value.includes(questionId)) {
        return false;
      }

      return true;
    }

    function buildEncodedReturnUrl(form: HTMLFormElement, url: string): string {
        const targetUrl = new URL(url, window.location.origin);
        const formData = new FormData(form);

        const mergedParams = new URLSearchParams(formData as any);

        for (const [key, value] of targetUrl.searchParams.entries()) {
          mergedParams.append(key, value);
        }

        const redirectUrl = `${form.action}?${mergedParams.toString()}`;

        return encodeURIComponent(redirectUrl);
    }

    return {
      id,
      survey,
      selectedRoutingAnswer,
      activeQuestionIndex,
      showSurvey,
      typeWriterText,
      updateSurvey,
      showLoader,
      nextQuestion,
      previousQuestion,
      startSurvey,
      isMobile,
      submitSurvey,
      formAction,
      factIconUrl,
      factTitle,
      isDisabled,
      updateQuestionsAnswered
    };
  }
};
</script>
